import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #pendingTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean | null} */
  #usesMobileOptionsLayout = null;

  /** @type {number | null} */
  #visibilityFrame = null;

  /** @type {boolean} */
  #isMirroringStickySelection = false;

  /** @type {{ windowX: number, windowY: number, wrapperX: number, wrapperY: number } | null} */
  #optionsToggleScrollPosition = null;

  #dockResize;
  #googleObserver;
  #googleWidget;
  #googleOriginal = new Map();

  #syncDock = () => {
    if (this.dataset.containedDock !== 'true') return;
    const mobile = this.isConnected && window.matchMedia('(max-width: 749px)').matches;
    const active = mobile && this.#isStuck;
    const root = document.documentElement;
    const stateChanged = root.classList.contains('dwil-contained-mobile-dock') !== mobile || root.classList.contains('dwil-contained-cart-visible') !== active;
    root.classList.toggle('dwil-contained-mobile-dock', mobile);
    root.classList.toggle('dwil-contained-cart-visible', active);
    root.classList.toggle('dwil-contained-options-open', active && this.dataset.optionsOpen === 'true');
    const summary = this.querySelector('.dwil-sticky-cart__summary');
    const height = active ? Math.ceil(summary?.getBoundingClientRect().height || 68) : 0;
    root.style.setProperty('--dwil-mobile-cart-height', height + 'px');
    if (active || !mobile) root.style.removeProperty('--dwil-mobile-dock-clearance');
    if (stateChanged) document.dispatchEvent(new Event('dwil:pdp-dock-change'));
    const widget = document.getElementById('google-merchantwidget-iframe-wrapper');
    if (widget !== this.#googleWidget) {
      this.#googleObserver?.disconnect();
      this.#googleWidget = widget;
      this.#googleOriginal.clear();
      if (widget) {
        for (const key of ['bottom', 'left']) this.#googleOriginal.set(key, [widget.style.getPropertyValue(key), widget.style.getPropertyPriority(key)]);
      }
      this.#googleObserver = new MutationObserver(this.#syncDock);
      this.#googleObserver.observe(widget || document.body, widget ? { attributes: true, attributeFilter: ['style'] } : { childList: true });
    }
    if (widget) {
      for (const key of ['bottom', 'left']) {
        const original = this.#googleOriginal.get(key) || ['', ''];
        // The compact Google badge has 16px of transparent space at its left and bottom edges.
        const value = mobile ? (key === 'bottom' ? 'calc(var(--dwil-mobile-cart-height, 0px) + env(safe-area-inset-bottom) + var(--dwil-mobile-dock-clearance, 0px) + 12px)' : '0px') : original[0];
        const priority = mobile ? 'important' : original[1];
        if (widget.style.getPropertyValue(key) !== value || widget.style.getPropertyPriority(key) !== priority) {
          if (value) widget.style.setProperty(key, value, priority); else widget.style.removeProperty(key);
        }
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener('change', this.#handleStickyVariantChange, { signal });
    this.addEventListener('keydown', this.#handleStickyKeydown, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    window.addEventListener('resize', this.#syncStickyOptionsViewport, { signal });
    window.addEventListener('scroll', this.#queueStickyVisibilityCheck, { passive: true, signal });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', this.#queueStickyVisibilityCheck, {
      passive: true,
      signal,
    });

    if (this.dataset.containedDock === 'true') {
      this.#dockResize = new ResizeObserver(this.#syncDock);
      const summary = this.querySelector('.dwil-sticky-cart__summary');
      if (summary) this.#dockResize.observe(summary);
      window.addEventListener('resize', this.#syncDock, { signal });
    }
    this.#getInitialQuantity();
    this.#syncStickyOptionsViewport();
    this.#queueStickyVisibilityCheck();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.dataset.containedDock === 'true') {
      this.#isStuck = false;
      this.#syncDock();
      this.#dockResize?.disconnect();
      this.#googleObserver?.disconnect();
      document.documentElement.style.removeProperty('--dwil-mobile-cart-height');
      document.documentElement.style.removeProperty('--dwil-mobile-dock-clearance');
    }
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.matches('.dwil-product-sticky, .dwil-pdp-sticky-cart, .dwil-universal-sticky-cart')) {
      document.documentElement.classList.remove('dwil-product-sticky-visible');
      document.documentElement.classList.remove('dwil-product-sticky-with-options');
    }
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#pendingTimeout) {
      clearTimeout(this.#pendingTimeout);
    }
    if (this.#visibilityFrame !== null) {
      cancelAnimationFrame(this.#visibilityFrame);
      this.#visibilityFrame = null;
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // The component can upgrade before the footer has finished parsing. The
    // core buy-button observer must still work when that optional guard is absent.
    const footer = this.#getPageFooter();
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            // Footer out of view - check if we should show sticky bar again
            const rect = buyButtonsBlock.getBoundingClientRect();
            // Only show if buy buttons are above the viewport (scrolled past)
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByBottom = false;
              if (!this.#isChatActive()) {
                this.#showStickyBar();
              }
            }
          }
        },
        {
          rootMargin: '200px 0px 0px 0px',
        }
      );
      this.#mainBottomObserver.observe(footer);
    }

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Supplements IntersectionObserver for Horizon's desktop scroll wrapper and
   * for mobile anchor navigation. Work is limited to one read per animation frame.
   */
  #queueStickyVisibilityCheck = () => {
    if (this.#visibilityFrame !== null) return;

    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = null;
      this.#syncStickyVisibilityFromPosition();
    });
  };

  #syncStickyVisibilityFromPosition() {
    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const buyButtonsRect = buyButtonsBlock.getBoundingClientRect();
    const footer = this.#getPageFooter();
    const footerRect = footer?.getBoundingClientRect();
    const footerIsNear = Boolean(
      footerRect && footerRect.height > 0 && footerRect.top <= window.innerHeight + 200 && footerRect.bottom >= 0
    );
    const shouldShow = buyButtonsRect.bottom < 0 && !footerIsNear && !this.#isChatActive();

    if (shouldShow && !this.#isStuck) {
      this.#hiddenByBottom = false;
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  #getPageFooter() {
    // Dialogs and product components may contain their own footer elements.
    return [...document.querySelectorAll('footer, [role="contentinfo"], .shopify-section-group-footer-group')]
      .find((footer) => !footer.closest('main, dialog, theme-drawer') && footer.getClientRects().length > 0);
  }

  // Public action handlers
  /**
   * Mirrors a sticky option selection to Horizon's native variant picker.
   * The native picker remains the single source of truth for product state.
   * @param {Event} event
   */
  #handleStickyVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement || target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-sticky-variant-select]')) return;

    const selectedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : null;
    const optionValueId = selectedOption?.dataset.optionValueId ?? target.dataset.optionValueId;
    if (!optionValueId) return;

    const sectionElement = this.closest('.shopify-section');
    const variantPicker = sectionElement?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantPicker) {
      this.dataset.variantLoading = 'false';
      return;
    }

    const nativeOption = Array.from(variantPicker.querySelectorAll('[data-option-value-id]')).find(
      (option) => option instanceof HTMLElement && option.dataset.optionValueId === optionValueId
    );
    if (!nativeOption) {
      this.dataset.variantLoading = 'false';
      return;
    }

    this.dataset.variantLoading = 'true';
    this.dataset.restoreOptionsAfterVariant = this.dataset.optionsOpen === 'true' ? 'true' : 'false';

    if (nativeOption instanceof HTMLInputElement) {
      if (nativeOption.disabled) {
        this.dataset.variantLoading = 'false';
        return;
      }
      if (nativeOption.checked) {
        this.dataset.variantLoading = 'false';
        return;
      }
      // The native option lives outside the sticky cart. Its programmatic
      // click bubbles to the document-level outside-click handler, which used
      // to collapse the options panel before the variant response restored it.
      // Mark only this synchronous mirror click so the shopper can keep the
      // panel open and continue choosing options without a close/reopen flash.
      this.#isMirroringStickySelection = true;
      try {
        nativeOption.click();
      } finally {
        this.#isMirroringStickySelection = false;
      }
      return;
    }

    if (nativeOption instanceof HTMLOptionElement && nativeOption.parentElement instanceof HTMLSelectElement) {
      if (nativeOption.disabled) {
        this.dataset.variantLoading = 'false';
        return;
      }
      if (nativeOption.parentElement.value === nativeOption.value) {
        this.dataset.variantLoading = 'false';
        return;
      }
      nativeOption.parentElement.value = nativeOption.value;
      nativeOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    this.dataset.variantLoading = 'false';
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#setOptionsOpen(false);
    this.#syncMainQuantity();
    this.#setPending(true);
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 1200);

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = variant?.available === true ? 'true' : 'false';
        const optionsWereOpen =
          this.dataset.optionsOpen === 'true' || this.dataset.restoreOptionsAfterVariant === 'true';

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;
        const stickyButton = this.refs.stickyBar.querySelector('[ref="addToCartButton"]');
        if (stickyButton instanceof HTMLButtonElement) {
          stickyButton.disabled = variant?.available !== true;
        }
        // A variant request can briefly resolve without a resource while Horizon
        // updates availability. That transient state must not close a panel the
        // shopper deliberately opened.
        this.#setOptionsOpen(optionsWereOpen, false);

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;

          // The server response can preserve the previous variant title while
          // Horizon is morphing several product components at once. The event
          // resource is the authoritative selected variant, so keep the compact
          // sticky summary in sync immediately instead of waiting for another
          // section render.
          const stickyVariantTitle = this.refs.stickyBar.querySelector('.dwil-sticky-cart__variant');
          if (stickyVariantTitle && variant.title) {
            stickyVariantTitle.textContent = variant.title;
          }
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#syncStickyQuantity();
        this.#updateButtonText();
        this.dataset.variantLoading = 'false';
        requestAnimationFrame(() => {
          this.#setOptionsOpen(optionsWereOpen, false);
          this.dataset.restoreOptionsAfterVariant = 'false';
        });
      })
      .catch(() => {
        this.dataset.restoreOptionsAfterVariant = 'false';
      })
      .finally(() => {
        this.dataset.variantLoading = 'false';
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector(
      '.sticky-add-to-cart__variant, .dwil-pdp-sticky-cart__variant, .dwil-sticky-cart__variant'
    );
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');
    const variantPicker = sectionElement?.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
      this.#setPending(false);
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;
    if (!(event.target instanceof Element)) return;

    const sourceStickyCart = event.target.closest('sticky-add-to-cart');
    if (sourceStickyCart === this) {
      this.#currentQuantity = event.detail.quantity;
      this.#syncMainQuantity();
      this.#updateButtonText();
      return;
    }

    const sourceProductForm = event.target.closest('product-form-component');
    if (!sourceProductForm || sourceProductForm !== this.#getProductForm()) return;

    this.#currentQuantity = event.detail.quantity;
    this.#syncStickyQuantity();
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncDock();
    if (this.matches('.dwil-product-sticky, .dwil-pdp-sticky-cart, .dwil-universal-sticky-cart')) {
      document.documentElement.classList.add('dwil-product-sticky-visible');
      document.documentElement.classList.toggle(
        'dwil-product-sticky-with-options',
        this.dataset.hasVariantOptions === 'true'
      );
    }
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#setOptionsOpen(false);
    if (this.matches('.dwil-product-sticky, .dwil-pdp-sticky-cart, .dwil-universal-sticky-cart')) {
      document.documentElement.classList.remove('dwil-product-sticky-visible');
      document.documentElement.classList.remove('dwil-product-sticky-with-options');
    }
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) {
      this.dataset.chatActive = 'false';
      return false;
    }

    const chatActive = Boolean(document.querySelector('shopify-chat'));
    this.dataset.chatActive = chatActive.toString();

    if (chatActive && this.dataset.allowWithChat === 'true') return false;
    return chatActive;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#syncStickyQuantity();
    this.#updateButtonText();
  }

  /**
   * Synchronizes the floating quantity back to the native product form.
   */
  #syncMainQuantity() {
    const mainQuantity = this.#getProductForm()?.querySelector('quantity-selector-component');
    if (mainQuantity && 'setValue' in mainQuantity && typeof mainQuantity.setValue === 'function') {
      mainQuantity.setValue(this.#currentQuantity.toString());
    }
  }

  /**
   * Synchronizes the native quantity into the floating selector without
   * dispatching another quantity event.
   */
  #syncStickyQuantity() {
    const stickyQuantity = this.querySelector('.dwil-sticky-cart__quantity quantity-selector-component');
    if (stickyQuantity && 'setValue' in stickyQuantity && typeof stickyQuantity.setValue === 'function') {
      stickyQuantity.setValue(this.#currentQuantity.toString());
    }
  }

  /**
   * Keeps options collapsed when the responsive mode changes so focus cannot
   * remain inside a newly hidden layout.
   */
  #syncStickyOptionsViewport = () => {
    const usesMobileLayout = window.matchMedia('(max-width: 749px)').matches;
    if (this.#usesMobileOptionsLayout === usesMobileLayout) return;

    this.#usesMobileOptionsLayout = usesMobileLayout;
    this.#setOptionsOpen(false);
  };

  /**
   * Captures the reading position before mobile browsers move focus to the
   * fixed disclosure control.
   * @param {PointerEvent} event
   */
  handleOptionsPointerDown = (event) => {
    if (event.button !== 0) return;
    const pageWrapper = document.querySelector('.page-wrapper');
    this.#optionsToggleScrollPosition = {
      windowX: window.scrollX,
      windowY: window.scrollY,
      wrapperX: pageWrapper instanceof HTMLElement ? pageWrapper.scrollLeft : 0,
      wrapperY: pageWrapper instanceof HTMLElement ? pageWrapper.scrollTop : 0,
    };
  };

  /** @param {MouseEvent} event */
  handleOptionsToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const pageWrapper = document.querySelector('.page-wrapper');
    const scrollPosition = this.#optionsToggleScrollPosition ?? {
      windowX: window.scrollX,
      windowY: window.scrollY,
      wrapperX: pageWrapper instanceof HTMLElement ? pageWrapper.scrollLeft : 0,
      wrapperY: pageWrapper instanceof HTMLElement ? pageWrapper.scrollTop : 0,
    };
    this.#optionsToggleScrollPosition = null;
    this.#setOptionsOpen(this.dataset.optionsOpen !== 'true');

    // Mobile browsers can try to reveal the source-positioned disclosure
    // control when a fixed panel changes height. Keep the customer's reading
    // position stable while the sticky options panel opens or closes.
    requestAnimationFrame(() => {
      if (window.scrollX !== scrollPosition.windowX || window.scrollY !== scrollPosition.windowY) {
        const scrollingElement = document.scrollingElement;
        if (scrollingElement) {
          scrollingElement.scrollLeft = scrollPosition.windowX;
          scrollingElement.scrollTop = scrollPosition.windowY;
        } else {
          window.scrollTo(scrollPosition.windowX, scrollPosition.windowY);
        }
      }
      if (
        pageWrapper instanceof HTMLElement &&
        (pageWrapper.scrollLeft !== scrollPosition.wrapperX || pageWrapper.scrollTop !== scrollPosition.wrapperY)
      ) {
        pageWrapper.scrollLeft = scrollPosition.wrapperX;
        pageWrapper.scrollTop = scrollPosition.wrapperY;
      }
    });
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (this.dataset.optionsOpen !== 'true') return;
    if (this.#isMirroringStickySelection) return;
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.#setOptionsOpen(false);
  };

  /** @param {KeyboardEvent} event */
  #handleStickyKeydown = (event) => {
    if (event.key !== 'Escape' || this.dataset.optionsOpen !== 'true') return;
    event.preventDefault();
    this.#setOptionsOpen(false, true);
  };

  /**
   * Updates both desktop and mobile disclosure controls and removes collapsed
   * controls from the accessibility tree.
   * @param {boolean} open
   * @param {boolean} [restoreFocus]
   */
  #setOptionsOpen(open, restoreFocus = false) {
    const panel = this.querySelector('[data-sticky-options-panel]');
    const toggles = Array.from(this.querySelectorAll('[data-sticky-options-toggle]'));
    const canOpen = open && panel instanceof HTMLElement && this.dataset.hasVariantOptions === 'true';

    this.dataset.optionsOpen = canOpen ? 'true' : 'false';
    this.#syncDock();
    if (panel instanceof HTMLElement) {
      panel.setAttribute('aria-hidden', canOpen ? 'false' : 'true');
      panel.inert = !canOpen;
    }
    toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', canOpen ? 'true' : 'false'));

    if (restoreFocus) {
      const visibleToggle = toggles.find((toggle) => toggle instanceof HTMLElement && toggle.offsetParent !== null);
      if (visibleToggle instanceof HTMLElement) visibleToggle.focus({ preventScroll: true });
    }
  }

  /** @param {boolean} pending */
  #setPending(pending) {
    if (this.#pendingTimeout) clearTimeout(this.#pendingTimeout);
    this.dataset.pending = pending ? 'true' : 'false';

    const button = this.refs.addToCartButton;
    button.setAttribute('aria-busy', pending ? 'true' : 'false');
    button.disabled = pending || this.dataset.variantAvailable !== 'true';

    if (pending) {
      this.#pendingTimeout = setTimeout(() => this.#setPending(false), 6000);
    }
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    if (quantityNumber) quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (!quantityDisplay) return;
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
